import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { cancelDeletion, confirmDeletion, errorKey } from "../../api/accountDeletion";
import { dangerButton, errorText, inputClass, linkButton, primaryButton } from "./styles";

export type Outcome =
    | { kind: "requested"; scheduledFor: string }
    | { kind: "cancelled" };

type Props = {
    token: string;
    pendingDeletion: boolean;
    scheduledFor: string | null;
    formatDate: (iso: string) => string;
    onDone: (outcome: Outcome) => void;
    onRestart: () => void;
};

const ConfirmStep: React.FC<Props> = ({
    token,
    pendingDeletion,
    scheduledFor,
    formatDate,
    onDone,
    onRestart,
}) => {
    const { t } = useTranslation();
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [reason, setReason] = useState("");
    const [understood, setUnderstood] = useState(false);

    const fail = (err: unknown) => setError(errorKey(err, "generic"));

    const onConfirm = async (e: React.FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
            const data = await confirmDeletion(token, reason.trim() || undefined);
            onDone({ kind: "requested", scheduledFor: data.scheduledFor });
        } catch (err) {
            fail(err);
        } finally {
            setBusy(false);
        }
    };

    const onCancel = async () => {
        setBusy(true);
        setError(null);
        try {
            await cancelDeletion(token);
            onDone({ kind: "cancelled" });
        } catch (err) {
            fail(err);
        } finally {
            setBusy(false);
        }
    };

    const errorBlock = error && (
        <div className={errorText}>
            {t(`delete_account.errors.${error}`)}{" "}
            {error === "session_expired" && (
                <button type="button" onClick={onRestart} className={linkButton}>
                    {t("delete_account.change")}
                </button>
            )}
        </div>
    );

    if (pendingDeletion)
        return (
            <div className="space-y-5">
                <h2 className="text-xl font-bold text-gray-900">{t("delete_account.pending_title")}</h2>
                <p className="text-gray-600">
                    {t("delete_account.pending_body", { date: scheduledFor ? formatDate(scheduledFor) : "" })}
                </p>
                {errorBlock}
                <button type="button" onClick={onCancel} disabled={busy} className={primaryButton}>
                    {t("delete_account.cancel_button")}
                </button>
            </div>
        );

    return (
        <form onSubmit={onConfirm} className="space-y-5">
            <h2 className="text-xl font-bold text-gray-900">{t("delete_account.confirm_title")}</h2>
            <p className="text-gray-600">{t("delete_account.timing_body")}</p>

            <label className="block">
                <span className="mb-2 block text-sm font-semibold text-gray-700">
                    {t("delete_account.reason_label")}
                </span>
                <textarea
                    rows={3}
                    maxLength={1000}
                    className={inputClass}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                />
            </label>

            <label className="flex items-start gap-3 text-gray-700">
                <input
                    type="checkbox"
                    className="mt-1 h-5 w-5 shrink-0 accent-red-600"
                    checked={understood}
                    onChange={(e) => setUnderstood(e.target.checked)}
                />
                <span>{t("delete_account.understand")}</span>
            </label>

            {errorBlock}

            <button type="submit" disabled={!understood || busy} className={dangerButton}>
                {t("delete_account.confirm_button")}
            </button>
        </form>
    );
};

export default ConfirmStep;
